import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import {
  withActiveId,
  withEntities,
  upsertEntities,
  setActiveId,
  getEntity,
  deleteEntities,
  selectEntity,
  getActiveId,
  selectAllEntities,
  selectMany,
  selectActiveEntity,
  getActiveEntity,
  selectActiveId,
  updateEntities,
} from '@ngneat/elf-entities';
import { entitiesStateHistory } from '@ngneat/elf-state-history';
import { Question } from '../models/question/question.model';
import { write } from './mutations';

const store = createStore(
  { name: 'renderer-questions' },
  withEntities<Question>(),
  withProps<{ hoverQuestion: string; isDragging: boolean }>({
    hoverQuestion: undefined,
    isDragging: false,
  }),
  withActiveId(),
);

@Injectable({ providedIn: 'root' })
export class RendererQuestionsRepository {
  questions$ = store.pipe(selectAllEntities());
  active$ = store.pipe(selectActiveEntity());
  activeId$ = store.pipe(selectActiveId());
  hoverQuestion$ = store.pipe(select((state) => state.hoverQuestion));
  isDragging$ = store.pipe(select((state) => state.isDragging));

  get(questionId: string) {
    return store.query(getEntity(questionId));
  }

  select(questionId: string) {
    return store.pipe(selectEntity(questionId));
  }

  selectMany(questionIds: string[]) {
    return store.pipe(selectMany(questionIds));
  }

  getActiveId() {
    return store.query(getActiveId);
  }

  getActive() {
    return store.query(getActiveEntity());
  }

  getIsDragging() {
    return store.query((state) => state.isDragging);
  }

  upsert(question: Question) {
    store.update(upsertEntities(question));
  }

  update(sectionId: string, updater: (state: Question) => void) {
    store.update(updateEntities(sectionId, write<Question>(updater)));
  }

  updateActive(updater: (state: Question) => void) {
    store.update(updateEntities(this.getActiveId(), write<Question>(updater)));
  }

  setActive(questionId: string) {
    store.update(setActiveId(questionId));
  }

  remove(questionIds: string[]) {
    store.update(deleteEntities(questionIds));
  }

  setHoverQuestion(questionId: string) {
    store.update(
      setProps(write((draft) => (draft.hoverQuestion = questionId))),
    );
  }

  setIsDragging(isDragging: boolean) {
    store.update(setProps(write((draft) => (draft.isDragging = isDragging))));
  }

  reset() {
    store.reset();
  }

  getHistoryCollection() {
    return entitiesStateHistory(store);
  }
}
